import Alert, { type AlertProps } from '@mui/material/Alert';
import Box, { type BoxProps } from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Grid, { type GridProps } from '@mui/material/Grid';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { csvDownload } from '@openx/components-core/utils/csvDownload.tsx';
import { type JSX, useState } from 'react';

import { mapDataTypeToTemplates } from './constants.ts';
import { type DataType, dataType as dataTypes } from './types.ts';

const InfoGrid = (props: GridProps) => (
  <Grid
    {...props}
    sx={theme => ({
      backgroundColor: theme.palette.info.light,
      display: 'flex',
      padding: theme.spacing(1.5, 2),
    })}
    container
  />
);

const StyledInfoAlert = (props: AlertProps) => (
  <Alert
    severity="info"
    {...props}
    sx={{
      '& .MuiAlert-message': {
        width: '100%',
      },
      alignItems: 'center',
      display: 'flex',
      height: 32,
      padding: 0,
      ...props.sx,
    }}
  />
);

const StyledParagraphBox = (props: BoxProps) => (
  <Box
    {...props}
    sx={[
      {
        mt: 3.5,
      },
      ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
    ]}
  />
);
const StyledLineTypography = (props: TypographyProps) => (
  <Typography
    component="span"
    {...props}
    sx={[
      {
        display: 'inline-block',
      },
      ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
    ]}
  />
);

export interface FormattingGuidelinesProps {
  dataType?: DataType;
}

export function FormattingGuidelines({ dataType }: FormattingGuidelinesProps): JSX.Element {
  const [open, setOpen] = useState(false);

  function onToggleButtonClick() {
    setOpen(!open);
  }

  function onDownloadTemplateButtonClick() {
    if (!dataType) {
      return;
    }

    csvDownload({ data: mapDataTypeToTemplates[dataType], filename: `${dataType}-template.csv` });
  }

  const toggleButtonLabel = open ? 'Hide details' : 'Show details';
  const showDataHashingInfo = dataType !== dataTypes.MOBILE_AD_ID;

  return (
    <InfoGrid>
      <Grid
        size={12}
        container
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <StyledInfoAlert
          data-test="formatting-alert"
          severity="info"
          sx={{ backgroundColor: 'transparent !important' }}
        >
          <Typography>Formatting guidelines and template.</Typography>
        </StyledInfoAlert>

        <Grid
          sx={{
            justifyContent: 'flex-end',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Button data-test="formating-detais-button" variant="text" color="primary" onClick={onToggleButtonClick}>
            {toggleButtonLabel}
          </Button>
        </Grid>
      </Grid>

      <Collapse in={open}>
        <Box data-test="details-info">
          {showDataHashingInfo && (
            <Box
              sx={{
                ml: 4.25,
              }}
            >
              <StyledParagraphBox>
                <StyledLineTypography>
                  Your data will be hashed locally on your browser before sending to us.
                </StyledLineTypography>
                <StyledLineTypography>If you want to pre-hash the data manually:</StyledLineTypography>
              </StyledParagraphBox>

              <StyledParagraphBox>
                <StyledLineTypography>
                  1. Normalize the data by converting all characters to lowercase and trimming all whitespace.
                </StyledLineTypography>
                <StyledLineTypography>2. Hash the normalized data using SHA256.</StyledLineTypography>
              </StyledParagraphBox>
            </Box>
          )}

          {dataType && (
            <Button
              data-test="download-template-button"
              size="small"
              onClick={onDownloadTemplateButtonClick}
              sx={{ marginBottom: 1.5, marginTop: 1.5 }}
            >
              Download template
            </Button>
          )}
        </Box>
      </Collapse>
    </InfoGrid>
  );
}
